import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from "react";
import { api } from "../api";
import { setCsrfToken, getCsrfToken, SESSION_EXPIRED_EVENT } from "../api/session";

interface User {
  user_id?: number;
  nome_completo?: string;
  email?: string;
  phone?: string;
  tipo?: "Comum" | "Colaborador" | "Admin";
}
export interface SessionResult { user: User; csrfToken: string; expiresAt: string; }
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  expiresAt: string | null;
  setAuthUser: (session: SessionResult) => void;
  logout: () => Promise<void>;
  updateUser: (userData: User) => void;
  getCsrfToken: () => string | null;
  getUserIdFromToken: () => number | null;
  checkAuthStatus: () => Promise<void>;
  getUserTipo: () => string | null;
  isAdmin: () => boolean;
  isComum: () => boolean;
  isColaborador: () => boolean;
  getProfileRoute: () => string;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  return context;
};
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const version = useRef(0);
  const clearAuth = useCallback(() => {
    version.current += 1;
    setCsrfToken(null);
    setUser(null);
    setExpiresAt(null);
    setLoading(false);
  }, []);
  const setAuthUser = useCallback((session: SessionResult) => {
    version.current += 1;
    setCsrfToken(session.csrfToken);
    setUser(session.user);
    setExpiresAt(session.expiresAt);
    setLoading(false);
  }, []);
  const checkAuthStatus = useCallback(async () => {
    const currentVersion = version.current;
    try {
      const response = await api.get<SessionResult>("/auth/session");
      if (version.current === currentVersion) setAuthUser(response.data);
    } catch (error: any) {
      if (version.current === currentVersion && error.response?.status === 401) clearAuth();
    } finally {
      // Uma consulta de uma montagem anterior não pode liberar as rotas
      // enquanto a sessão da montagem atual ainda está sendo restaurada.
      if (version.current === currentVersion) setLoading(false);
    }
  }, [setAuthUser, clearAuth]);
  const logout = useCallback(async () => {
    try { await api.post("/logout"); }
    catch (error: any) { if (error.response?.status !== 401) throw error; }
    clearAuth();
  }, [clearAuth]);
  useEffect(() => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("userData");
    window.addEventListener(SESSION_EXPIRED_EVENT, clearAuth);
    void checkAuthStatus();
    return () => { version.current += 1; window.removeEventListener(SESSION_EXPIRED_EVENT, clearAuth); };
  }, [checkAuthStatus, clearAuth]);
  useEffect(() => {
    if (!expiresAt) return;
    const timer = window.setTimeout(clearAuth, Math.max(0, Date.parse(expiresAt) - Date.now()));
    return () => window.clearTimeout(timer);
  }, [expiresAt, clearAuth]);
  useEffect(() => {
    if (!user) return;
    const refresh = () => { if (document.visibilityState === "visible") void checkAuthStatus(); };
    const timer = window.setInterval(() => void checkAuthStatus(), 60000);
    window.addEventListener("focus", refresh);
    return () => { window.clearInterval(timer); window.removeEventListener("focus", refresh); };
  }, [user, checkAuthStatus]);
  return <AuthContext.Provider value={{
    user, loading, expiresAt, isAuthenticated: !!user,
    setAuthUser, logout, updateUser: setUser, getCsrfToken, checkAuthStatus,
    getUserIdFromToken: () => user?.user_id || null, getUserTipo: () => user?.tipo || null,
    isAdmin: () => user?.tipo === "Admin", isComum: () => user?.tipo === "Comum",
    isColaborador: () => user?.tipo === "Colaborador",
    getProfileRoute: () => user?.tipo === "Admin" ? "/profile-admin" : user?.tipo === "Colaborador" ? "/profile-colaborador" : "/profile-usuario-comum",
  }}>{children}</AuthContext.Provider>;
};
